// # Xform
//
// A combination of location and orientation.
//

import { Point } from "./point";
import { Bbox } from "./bbox";
import { Direction } from "./direction";
import { Orientation } from "./orientation";
import { schema } from "../schema";
import { Matrix } from "./matrix";

export type Xform = schema.Xform;

export const Xform = {
  new: create,
  create,
  loc,
  orientation,

  fromParts: (loc: Point, orientation: Orientation): Xform => {
    return { ...loc, ...orientation };
  },

  default: (): Xform => {
    return { ...Point.origin(), ...Orientation.default() };
  },

  eq: (a: Xform, b: Xform): boolean => Point.eq(a, b) && Orientation.eq(a, b),

  clone: (xform: Xform): Xform => ({
    x: xform.x,
    y: xform.y,
    reflected: xform.reflected,
    rotation: xform.rotation,
  }),

  // Apply the transformation to a point
  transform: (xform: Xform, pt: Point): Point => {
    const mat = Orientation.toMatrix(xform);
    return Point.new(
      mat.a * pt.x + mat.c * pt.y + xform.x,
      mat.b * pt.x + mat.d * pt.y + xform.y
    );
  },

  // Apply the transformation to a bounding box
  transformBbox: (xform: Xform, bbox: Bbox): Bbox => {
    const minXform = Xform.transform(xform, bbox.min);
    const maxXform = Xform.transform(xform, bbox.max);
    // Note this can swap the roles of `min` and `max`, in either direction.
    return Bbox.fromTwoPoints([minXform, maxXform]);
  },

  relative: (xform: Xform, relativeTo: Xform): Xform => {
    const loc = Point.sub(xform, relativeTo);
    const orientation = Orientation.relative(xform, relativeTo);
    return { ...loc, ...orientation };
  },

  // Create an inverse transform helper
  inverse: (xform: Xform): Inverse => {
    return new Inverse(xform);
  },
};

export function create(
  x: number,
  y: number,
  reflected: boolean,
  rotation: schema.Rotation
): Xform {
  return { x, y, reflected, rotation };
}

// Extract (only) the `Point` fields from a transform
export function loc(xform: Xform): Point {
  const { x, y } = xform;
  return { x, y };
}

// Extract (only) the `Orientation` fields from a transform
export function orientation(xform: Xform): Orientation {
  const { reflected, rotation } = xform;
  return { reflected, rotation };
}

//
// # Inverse Transform
//
// You may think, why is that a type, not just another `Xform`,
// and maybe the result of like an `inverse` method?
// Because this is faster.
//
// Our transforms are very limited subsets of all the possible 2D transforms,
// especially WRT orientation. Only 8 possible orientations are valid.
// Calculating a general-purpose inverse take a little bit of math,
// particularly matrix inversion.
//
// *Applying* one of these in contrast, just requires applying the two
// "forward transform" operations - translation and orientation -
// in the opposite order.
//
// Hence the separate class, and particularly the `apply` method.
//
export class Inverse {
  // Orientation matrix (initialized in constructor)
  readonly matrix: Matrix;

  constructor(readonly of: Xform) {
    this.matrix = Orientation.toMatrix(Orientation.inverse(of));
  }

  // Apply the inverse to a point
  apply(pt: Point): Point {
    // (a) Apply inverse translation (i.e. subtract our translation) first
    const translated = Point.sub(pt, this.of);

    // (b) Apply the inverse orientation
    const { matrix } = this;
    return Point.new(
      matrix.a * translated.x + matrix.c * translated.y,
      matrix.b * translated.x + matrix.d * translated.y
    );
  }

  // Apply the inverse to a bounding box
  applyToBbox(bbox: Bbox): Bbox {
    const min = this.apply(bbox.min);
    const max = this.apply(bbox.max);
    return Bbox.fromTwoPoints([min, max]);
  }
}

// # Xformable
//
// Interface for things that have a transformed,
// and can have that `Xform` updated in a few ways.
//
export interface Xformable {
  // Get the current placement
  xform: Xform;
  // Rotate by one 90 degree increment
  rotate(): void;
  // Flip in direction `dir`
  flip(dir: Direction): void;
}

export interface HasXform {
  xform: Xform;
}
